import { useCallback, useLayoutEffect, useRef, useState } from 'react'

/** IAB-style billboard used by `billboard_carousel` promos and shop carousel. */
export const BILLBOARD_CAROUSEL_WIDTH = 1200
export const BILLBOARD_CAROUSEL_HEIGHT = 362

export type BillboardCarouselSlot = {
  scale: number
  heightPx: number
}

/**
 * Measures a full-width slot and returns scale + pixel height for a 1200×362 creative:
 * - slot wider than 1200px: fixed 362px height, scale > 1 (clip overflow)
 * - narrower: height scales down with width so there is no vertical letterboxing
 */
export function useBillboardCarouselSlot(active: boolean) {
  const [slot, setSlot] = useState<BillboardCarouselSlot>({
    scale: 1,
    heightPx: BILLBOARD_CAROUSEL_HEIGHT,
  })
  const containerRef = useRef<HTMLDivElement | null>(null)

  const setContainerNode = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node
  }, [])

  useLayoutEffect(() => {
    if (!active) return
    const el = containerRef.current
    if (!el) return

    const update = () => {
      const w = el.getBoundingClientRect().width
      const scale = w > 0 ? w / BILLBOARD_CAROUSEL_WIDTH : 1
      const rawHeight = scale > 1 ? BILLBOARD_CAROUSEL_HEIGHT : BILLBOARD_CAROUSEL_HEIGHT * scale
      const heightPx = Math.ceil(rawHeight)
      setSlot({ scale, heightPx })
    }

    let rafId = 0
    const scheduleRemeasure = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = 0
        update()
      })
    }

    update()
    scheduleRemeasure()

    const ro = new ResizeObserver(() => {
      update()
      scheduleRemeasure()
    })
    ro.observe(el)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      ro.disconnect()
    }
  }, [active])

  return { slot, setContainerNode }
}
